import React, { useEffect, useContext, useRef, useState, useMemo, useCallback } from 'react';
import { closeOutline } from 'ionicons/icons';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  IonModal,
  IonContent,
  IonHeader,
  IonButton,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonTitle,
  IonIcon,
} from '@ionic/react';
import { AppContext } from '../../global/AppContext';
import { acceptFight, createPayment, createTestPayment, TAKEOUT_TYPES } from '../../global/request/bet';
import { getPastAddresses } from '../../global/request/user';
import useRestaurants from '../../global/useRestaurants';
import {
  areAddressesInRange,
  calculateGrandTotal,
  DELIVERY_RANGE,
  DELIVERY_SERVICE_FEE_CENTS,
  DELIVERY_TIP_PERCENT,
  parseFormattedAddress,
  SALES_TAX_PERCENT,
  IN_HOUSE_SERVICE_FEE_CENTS,
} from '../../global/util';
import useGoogleMapsLoader from '../../googleMapsApi/useGoogleMapsLoader';
import AddressFields from '../addressFields/AddressFields';
import { AuthContext } from '../authentication/AuthContext';
import TakerError from '../fight/takerFightProcess/TakerError';
import BetStripeForm from './BetStripeForm';

import '../../styles/home/BetDetailModal.scss';
import '../../styles/fight/summary/GamePanel.scss';

const ModalAfterEarlyReturn = ({
  bet,
  isOpen,
  onClose,
  modalAnimationClass,
  onFightClick,
  formatName,
  // eslint-disable-next-line no-unused-vars
  pendingFight,
}) => {
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);

  const [address, setAddress] = useState(appCtx?.deliveryAddress?.trim() || '');
  const [addressDetails, setAddressDetails] = useState({
    streetAddress: '',
    apt: '',
    city: '',
    state: '',
    zip: '',
  });
  const [pastAddresses, setPastAddresses] = useState([]);
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [showAddressFields, setShowAddressFields] = useState(false);
  const deliveryInstructionsRef = useRef();
  const [paymentId, setPaymentId] = useState('');
  const stripeFormRef = useRef(null);
  const [clientSecret, setClientSecret] = useState('');
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_LIVE_KEY);
  // window.location.hostname === 'www.getfoodfight.link'
  //   ? loadStripe(process.env.REACT_APP_STRIPE_LIVE_KEY)
  //   : loadStripe(process.env.REACT_APP_STRIPE_TEST_KEY);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isDelivery, setIsDelivery] = useState(false);

  const [, restaurants] = useRestaurants();

  const [restaurantAddress, setRestaurantAddress] = useState();

  const { isLoaded } = useGoogleMapsLoader();
  const geocoderRef = useRef();

  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const appearance = {
    theme: 'stripe',
  };

  const options = {
    clientSecret,
    appearance,
  };

  const {
    // eslint-disable-next-line no-unused-vars
    makerName,
    restaurantItems,
    makerOutcomeTeamColor,
    takerOutcomeTeamColor,
    makerTeamAcronym,
    takerTeamAcronym,
    takerOutcomePhrase,
  } = bet;

  const totalPrice = useMemo(
    () => restaurantItems.map(item => item.price * item.quantity).reduce((sum, cost) => sum + cost),
    [restaurantItems],
  );
  const grandTotal = useMemo(() => calculateGrandTotal(totalPrice, isDelivery), [totalPrice, isDelivery]);

  const isPaymentRequired = useMemo(() => totalPrice > 0, [totalPrice]);

  const itemsWithIds = restaurantItems.map((item, index) => ({ ...item, tempId: index }));

  const fetchPastAddresses = useCallback(async () => {
    setPastAddresses(await getPastAddresses(authCtx.tokens.idToken));
  }, [authCtx.tokens.idToken, setPastAddresses]);

  useEffect(() => {
    (async () => {
      setIsDelivery(bet.takeoutType === TAKEOUT_TYPES.DELIVERY);
    })();
  }, [bet?.takeoutType]);

  const submitForm = async () => {
    if (stripeFormRef.current) {
      await stripeFormRef.current.requestSubmit();
    }
  };

  const sendPaymentInfo = changedId => {
    setPaymentId(changedId);
  };

  const handleFightClick = async () => {
    if (hasSubmitted) return;

    const trimmedApt = addressDetails.apt?.replace(' ', '');
    const formattedAddress = `${addressDetails.streetAddress}${trimmedApt ? ` ${trimmedApt}` : ''}, ${
      addressDetails.city
    }, ${addressDetails.state} ${addressDetails.zip}`;

    if (isDelivery) {
      if (!isAddressValid) {
        setAddressError(true);
        setErrorMessage('Please input valid address.');
        setIsErrorOpen(true);
        return;
      }
      if (!restaurantAddress) {
        console.error('Restaurant address not found.');
        setErrorMessage('Restaurant address not found. Please reselect a venue.');
        setIsErrorOpen(true);
        return;
      }
      if (!isLoaded) {
        console.error('Google Maps API is not loaded.');
        setErrorMessage('Internal error. Please refresh and try again.');
        setIsErrorOpen(true);
        return;
      }
      if (!(await areAddressesInRange(geocoderRef.current, restaurantAddress, formattedAddress, DELIVERY_RANGE))) {
        console.warn('[BetDetailsModal] handleFightClick: address not in restaurant delivery range');
        setErrorMessage('Given address is not within range of venue.');
        setIsErrorOpen(true);
        return;
      }
    }

    setHasSubmitted(true);
    try {
      // trigger stripe form with single button
      if (isPaymentRequired) {
        await submitForm();
      } else {
        // hard coding menu items as [] because they can't be modified from matchmaking currently

        if (onFightClick) {
          await onFightClick(
            bet.id,
            paymentId,
            formattedAddress.trim(),
            [],
            deliveryInstructionsRef.current?.value || '',
          );
        }

        setTimeout(() => {
          acceptFight(
            authCtx.tokens.idToken,
            bet.id,
            paymentId,
            formattedAddress.trim(),
            [],
            deliveryInstructionsRef.current?.value || '',
          );
          onClose();
        }, 1000);
      }
    } catch (error) {
      console.error('Error during submission', error);
      setHasSubmitted(false);
    }
  };

  const handleSetAddress = () => {
    if (!isAddressValid) {
      console.error('address in invalid format');
      return;
    }

    const trimmedApt = addressDetails.apt?.replace(' ', '');
    const formattedAddress = `${addressDetails.streetAddress}${trimmedApt ? ` ${trimmedApt}` : ''}, ${
      addressDetails.city
    }, ${addressDetails.state} ${addressDetails.zip}`;

    setAddress(formattedAddress);

    setShowAddressFields(false);

    setAddressError(false);
  };

  const sendPayment = useCallback(async () => {
    if (isPaymentRequired) {
      let totalAmount = 0;
      restaurantItems.forEach(item => {
        totalAmount += item.price * item.quantity;
      });
      const payment =
        window.location.hostname === 'www.getfoodfight.link'
          ? await createPayment(authCtx.tokens.idToken, totalAmount, bet.takeoutType === TAKEOUT_TYPES.DELIVERY)
          : await createTestPayment(authCtx.tokens.idToken, totalAmount, bet.takeoutType === TAKEOUT_TYPES.DELIVERY);

      setClientSecret(payment);
    }
  }, [isPaymentRequired, restaurantItems, authCtx.tokens.idToken, bet.takeoutType, setClientSecret]);

  useEffect(() => {
    sendPayment();
  }, [isPaymentRequired, sendPayment]);

  useEffect(() => {
    if (isLoaded) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (!restaurants || restaurants.length === 0) {
      return;
    }

    const betRestaurant = restaurants.find(restaurant => restaurant.id === bet.restaurantId);
    setRestaurantAddress(betRestaurant.address);
  }, [restaurants, bet.restaurantId]);

  useEffect(() => {
    setAddress(appCtx.deliveryAddress);

    const addrDetails = parseFormattedAddress(appCtx.deliveryAddress);

    if (!addrDetails.invalid) {
      setAddressDetails(addrDetails);
      setIsAddressValid(true);
    } else {
      setIsAddressValid(false);
    }

    fetchPastAddresses();
  }, [appCtx.deliveryAddress, authCtx?.tokens, fetchPastAddresses]);

  useEffect(() => {
    if (paymentId !== '') {
      // hard coding menu items as [] because they can't be modified from matchmaking currently

      if (onFightClick) {
        onFightClick(bet.id, paymentId, address || '', [], deliveryInstructionsRef.current?.value || '');
      }

      setTimeout(() => {
        acceptFight(
          authCtx.tokens.idToken,
          bet.id,
          paymentId,
          address || '',
          [],
          deliveryInstructionsRef.current?.value || '',
        );
        onClose();
      }, 1000);
    }
  }, [paymentId, address, authCtx.tokens.idToken, bet.id, onClose, onFightClick]);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className={modalAnimationClass}>
      <IonHeader className='bet-details_modal_header'>
        <IonToolbar className='bet-details_modal_header_toolbar'>
          <IonButtons slot='start'>
            <IonBackButton />
          </IonButtons>
          <IonTitle className='bet-details_title bet-details_font'>FIGHT SUMMARY</IonTitle>
          <IonButtons slot='end'>
            <IonButton onClick={onClose}>
              <IonIcon icon={closeOutline} className='close' slot='icon-only' />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className='details-modal-ion-content'>
        <div className='content-wrapper'>
          <div className='bet-details_font bet-details_header'>MATCHUP</div>
          <div
            className='game-panel'
            style={{
              '--left-color': takerOutcomeTeamColor,
              '--right-color': makerOutcomeTeamColor,
            }}>
            <div className='bet-details_teams'>
              <div className='bet-details_leftTeam team' style={{ background: takerOutcomeTeamColor }}>
                <p>YOU</p>
                <h1 className='bet-details_font'>{takerTeamAcronym}</h1>
              </div>
              <div className='gap'>
                <div className='panel-vs-circle'>
                  <h1 className='bet-modal-circle'>vs</h1>
                </div>
              </div>
              <div className='bet-details_rightTeam team' style={{ background: makerOutcomeTeamColor }}>
                <p>{formatName(makerName)}</p>
                <h1 className='bet-details_font'>{makerTeamAcronym}</h1>
              </div>
            </div>
          </div>
          <div className='bet-details_font'>YOUR OUTCOME</div>
          <div className='bet-details_outcome'>{takerOutcomePhrase}</div>
          <div>
            <div>
              <div className='bet-details_font'>Fight Cart</div>
              <ul className='bet-details_list'>
                {itemsWithIds.map(item => (
                  <li key={item.tempId}>
                    <div className='fight-items'>
                      <div className='item-name'>{item.name}</div>
                      {item.price > 0 ? <div className='item-price'>${(item.price / 100.0).toFixed(2)}</div> : <div />}
                      <div className='item-quantity'>{item.quantity}</div>
                    </div>
                  </li>
                ))}
              </ul>
              {isPaymentRequired && (
                <div className='price-breakdown'>
                  <div className='price-portion'>
                    <p className='price-portion-name'>Subtotal</p>
                    <p className='price-portion-value'>${(totalPrice / 100).toFixed(2)}</p>
                  </div>
                  {isDelivery ? (
                    <div className='price-portion'>
                      <p className='price-portion-name'>Delivery + Sevice Fee</p>
                      <p className='price-portion-value'>${(DELIVERY_SERVICE_FEE_CENTS / 100).toFixed(2)}</p>
                    </div>
                  ) : (
                    <div className='price-portion'>
                      <p className='price-portion-name'>Sevice Fee</p>
                      <p className='price-portion-value'>${(IN_HOUSE_SERVICE_FEE_CENTS / 100).toFixed(2)}</p>
                    </div>
                  )}
                  <div className='price-portion'>
                    <p className='price-portion-name'>Sales Tax (11.5%)*</p>
                    <p className='price-portion-value'>${((totalPrice * SALES_TAX_PERCENT) / 100).toFixed(2)}</p>
                  </div>
                  <div className='price-portion'>
                    <p className='price-portion-name'>Tip</p>
                    <p className='price-portion-value'>${((totalPrice * DELIVERY_TIP_PERCENT) / 100).toFixed(2)}</p>
                  </div>
                  )
                  <div className='price-total'>
                    <p className='price-total-name'>Total</p>
                    <p className='price-total-value'>${(grandTotal / 100).toFixed(2)}</p>
                  </div>
                  {isDelivery && (
                    <p className='sales-tax-disclaimer'>
                      *Sales tax amount is a temporary hold based on the highest sales tax rate in the US. You will only
                      be charged for the sales tax rate in your opponent&apos;s city if your opponent wins.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          {pendingFight === 1 && isDelivery && (
            <div className='address-container'>
              <div className='bet-details_font'>Address</div>
              {!showAddressFields ? (
                <p className='address-text'>
                  {isAddressValid ? address : <span className='invalid-address-text'>Invalid Address</span>}
                  <button type='button' onClick={() => setShowAddressFields(true)} className='change-text'>
                    Change Address
                  </button>
                </p>
              ) : (
                <>
                  <AddressFields
                    addressDetails={addressDetails}
                    setAddressDetails={setAddressDetails}
                    setIsFormValid={setIsAddressValid}
                    showAutocompleteDefault={false}
                    pastAddresses={pastAddresses}
                  />
                  <IonButton expand='block' onClick={handleSetAddress} className='set-address-button'>
                    Set Address
                  </IonButton>
                </>
              )}
              {addressError && <div className='error-message'>Address is required</div>}
              <textarea
                placeholder="Delivery instructions. i.e. 'Leave at door'"
                ref={deliveryInstructionsRef}
                className='delivery-instructions-textbox'
              />
            </div>
          )}
          {clientSecret && isPaymentRequired ? (
            <div>
              <div className='bet-details_font'>Payment Information</div>
              {clientSecret && (
                <Elements options={options} stripe={stripePromise}>
                  <BetStripeForm formRef={stripeFormRef} sendPaymentInfo={sendPaymentInfo} />
                </Elements>
              )}
              <p className='disclaimer-text'>
                By proceeding you authorize a 48 hr hold on your card for the amount listed in the Fight Cart. Win =
                authorization canceled; Lose = authorization processed.
              </p>
            </div>
          ) : (
            <div />
          )}
          <IonButton className='fight-button' type='button' onClick={handleFightClick} disabled={hasSubmitted}>
            Fight
          </IonButton>
        </div>
      </IonContent>
      <TakerError
        isOpen={isErrorOpen}
        onClose={() => setIsErrorOpen(false)}
        setIsOpen={setIsErrorOpen}
        msg={errorMessage}
      />
    </IonModal>
  );
};

const BetDetailsModal = ({ bet, isOpen, onClose, modalAnimationClass, onFightClick, formatName, pendingFight }) => {
  if (!bet) return null;
  return (
    <ModalAfterEarlyReturn
      bet={bet}
      isOpen={isOpen}
      onClose={onClose}
      modalAnimationClass={modalAnimationClass}
      onFightClick={onFightClick}
      formatName={formatName}
      pendingFight={pendingFight}
    />
  );
};

export default BetDetailsModal;
